import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AsyncButton } from '@/components/common/AsyncButton';
import { useToast } from '@/components/common/toast';
import { formatDateOnly, formatDateTime } from '@/utils/format';
import type { ProviderStatementConfig } from './config';
import { UploadExcelDialog } from './UploadExcelDialog';

interface Props {
  config: ProviderStatementConfig;
  can: {
    uploadExcel: boolean;
    manualSync: boolean;
  };
  // Live counts from the two Unmatched grids (recordsTotal).
  unmatchedStatementsCount: number;
  unmatchedTransactionsCount: number;
  onReloadAll: () => void;
}

export function StatisticsTab({
  config,
  can,
  unmatchedStatementsCount,
  unmatchedTransactionsCount,
  onReloadAll,
}: Props) {
  const toast = useToast();
  const { api, title, supportsApiSync } = config;
  const [uploadOpen, setUploadOpen] = useState(false);

  const summary = useQuery({
    queryKey: ['provider-statement', config.key, 'summary'],
    queryFn: api.getSummaryData,
  });

  const uploadMutation = useMutation({
    mutationFn: (args: { file: File; comment: string }) => api.uploadExcel(args.file, args.comment),
    onMutate: () => toast.info('Your data is being processed. This may take a few minutes.'),
    onSuccess: () => {
      toast.success('File uploaded successfully.');
      setUploadOpen(false);
      onReloadAll();
    },
    onError: () => toast.error('Upload failed.'),
  });

  const apiSyncMutation = useMutation({
    mutationFn: () => api.processApiStatements!(),
    onSuccess: (res) => {
      toast.success(res.message || 'Sync completed.');
      onReloadAll();
    },
    onError: () => toast.error('Sync failed.'),
  });

  const data = summary.data;
  const dash = (s: string) => s || 'N/A';

  return (
    <div className="row g-3">
      <div className="col-12">
        <div className="d-flex flex-wrap align-items-center gap-2">
          {can.uploadExcel && (
            <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-upload" onClick={() => setUploadOpen(true)}>
              Upload
            </AsyncButton>
          )}
          <AsyncButton
            className="btn btn-outline-primary btn-sm"
            icon="fa fa-download"
            onClick={() => api.downloadExtract(`${title} Statement.xlsx`)}
          >
            Extract
          </AsyncButton>
          {can.manualSync && supportsApiSync && (
            <AsyncButton
              className="btn btn-outline-primary btn-sm"
              icon="fa fa-sync"
              loading={apiSyncMutation.isPending}
              onClick={() => apiSyncMutation.mutateAsync()}
            >
              API - Manual Sync
            </AsyncButton>
          )}
        </div>
      </div>

      <div className="col-12 col-lg-6">
        <div className="card custom-card mb-0">
          <div className="card-header border-bottom">
            <h6 className="card-title mb-0">Summary</h6>
          </div>
          <div className="card-body p-0">
            <table className="table table-striped mb-0">
              <tbody>
                <tr>
                  <td>Starting date</td>
                  <td className="text-end">{dash(formatDateOnly(data?.startingDate))}</td>
                </tr>
                <tr>
                  <td>Last upload date</td>
                  <td className="text-end">{dash(formatDateTime(data?.lastUploadDate))}</td>
                </tr>
                <tr>
                  <td>Captured Platform Transactions Records</td>
                  <td className="text-end">{(data?.allTransactionsCount ?? 0).toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="col-12 col-lg-6">
        <div className="card custom-card mb-0">
          <div className="card-header border-bottom">
            <h6 className="card-title mb-0">Matching Results</h6>
          </div>
          <div className="card-body p-0">
            <table className="table table-striped mb-0">
              <tbody>
                <tr>
                  <td>Matched Platform Transactions</td>
                  <td className="text-end">{(data?.matchedCount ?? 0).toLocaleString()}</td>
                </tr>
                <tr>
                  <td>Provider Transactions Not Found</td>
                  <td className="text-end">{unmatchedStatementsCount.toLocaleString()}</td>
                </tr>
                <tr>
                  <td>Unmatched Platform Transactions</td>
                  <td className="text-end">{unmatchedTransactionsCount.toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <UploadExcelDialog
        show={uploadOpen}
        providerTitle={title}
        busy={uploadMutation.isPending}
        onConfirm={(file, comment) => uploadMutation.mutate({ file, comment })}
        onCancel={() => setUploadOpen(false)}
      />
    </div>
  );
}
